/**
 * Mengaji Streak - Duolingo-style daily Quran reading streak
 * Misi Harian: minimal 5 ayat per hari untuk streak.
 * Uses localStorage (no auth required). Optional API sync later.
 * All dates use user's local timezone.
 */
import { getLocalDateString } from "../utils/dateUtils";

const STORAGE_KEY = "mengaji_streak_dates";
const VERSES_READ_KEY = "mengaji_verses_read";
const MIN_VERSES_FOR_STREAK = 5;

/** Parse YYYY-MM-DD as local calendar date (no UTC shift). */
function parseLocalYmd(yyyyMmDd) {
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Format local Date to YYYY-MM-DD */
function formatLocalYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Add days to a local calendar date string */
function addDaysYmd(yyyyMmDd, delta) {
  const x = parseLocalYmd(yyyyMmDd);
  x.setDate(x.getDate() + delta);
  return formatLocalYmd(x);
}

/** @returns {string} YYYY-MM-DD for today (user's local timezone) */
function todayStr() {
  return getLocalDateString();
}

/** Load verses read per day: { "2026-03-19": ["1:1","2:3",...] } */
function loadVersesRead() {
  try {
    const raw = localStorage.getItem(VERSES_READ_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveVersesRead(data) {
  try {
    localStorage.setItem(VERSES_READ_KEY, JSON.stringify(data));
  } catch {}
}

/** Load stored dates from localStorage */
function loadDates() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Save dates to localStorage (dedupe, sort desc) */
function saveDates(dates) {
  const unique = [...new Set(dates)].sort().reverse();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(unique));
  } catch {}
  return unique;
}

/**
 * Record mengaji for today. Idempotent (same day = no duplicate).
 * Call when user reads Quran (verse visible, surah opened, etc.)
 * @returns {{ recorded: boolean, currentStreak: number, longestStreak: number }}
 */
export function recordMengajiToday() {
  const today = todayStr();
  let dates = loadDates();
  const alreadyRecorded = dates.includes(today);
  if (!alreadyRecorded) {
    dates = saveDates([...dates, today]);
  }
  const { currentStreak, longestStreak, liveStreak } = computeStreaks(dates);
  return {
    recorded: !alreadyRecorded,
    currentStreak,
    longestStreak,
    liveStreak,
    totalDays: dates.length,
  };
}

/**
 * Consecutive qualified days ending on `endDate` (endDate must be in `dates`).
 */
function streakEndingOn(datesSet, endDate) {
  if (!datesSet.has(endDate)) return 0;
  let run = 1;
  let cur = endDate;
  while (datesSet.has(addDaysYmd(cur, -1))) {
    run++;
    cur = addDaysYmd(cur, -1);
  }
  return run;
}

/**
 * Compute current streak (consecutive qualified days ending today only).
 * Strict: if today not yet 5+ ayat, returns 0.
 */
function computeStreaks(dates) {
  if (dates.length === 0)
    return { currentStreak: 0, longestStreak: 0, liveStreak: 0 };

  const today = todayStr();
  const yesterday = addDaysYmd(today, -1);
  const sorted = [...dates].sort();
  const set = new Set(sorted);

  // Strict streak: only counts when today's quota is already met
  const currentStreak = streakEndingOn(set, today);

  // Display streak: today qualified OR still "alive" (yesterday qualified, today open)
  let liveStreak = currentStreak;
  if (liveStreak === 0 && set.has(yesterday)) {
    liveStreak = streakEndingOn(set, yesterday);
  }

  // Longest streak: scan sorted ascending for consecutive calendar runs
  let longestStreak = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    if (addDaysYmd(sorted[i - 1], 1) === sorted[i]) {
      run++;
      longestStreak = Math.max(longestStreak, run);
    } else {
      run = 1;
    }
  }

  return { currentStreak, longestStreak, liveStreak };
}

/**
 * Get current streak stats without recording.
 * @returns {{ currentStreak: number, longestStreak: number, liveStreak: number, totalDays: number, todayRecorded: boolean }}
 */
export function getStreakStats() {
  const dates = loadDates();
  const { currentStreak, longestStreak, liveStreak } = computeStreaks(dates);
  const today = todayStr();
  return {
    currentStreak,
    longestStreak,
    liveStreak,
    totalDays: dates.length,
    todayRecorded: dates.includes(today),
  };
}

/**
 * Record a verse as read. Only counts toward streak when >= MIN_VERSES_FOR_STREAK.
 * @param {number} surahNumber
 * @param {number} verseNumber
 * @returns {{ recorded: boolean, currentStreak: number, versesReadToday: number }}
 */
export function recordVerseRead(surahNumber, verseNumber) {
  const today = todayStr();
  const key = `${surahNumber}:${verseNumber}`;
  const data = loadVersesRead();
  const todayList = data[today] || [];
  if (todayList.includes(key)) {
    const streakStats = getStreakStats();
    return {
      recorded: false,
      currentStreak: streakStats.currentStreak,
      versesReadToday: todayList.length,
    };
  }
  const updated = [...todayList, key];
  data[today] = updated;
  saveVersesRead(data);

  let recorded = false;
  if (updated.length >= MIN_VERSES_FOR_STREAK) {
    const res = recordMengajiToday();
    recorded = res.recorded;
  }

  const streakStats = getStreakStats();
  return {
    recorded,
    currentStreak: streakStats.currentStreak,
    versesReadToday: updated.length,
  };
}

/**
 * Get mengaji mission stats for Misi Harian widget (verses today, streak, todayRecorded).
 * @returns {{ todayRecorded: boolean, versesReadToday: number, currentStreak: number, longestStreak: number, liveStreak: number }}
 */
export function getMengajiMissionStats() {
  const streakStats = getStreakStats();
  const data = loadVersesRead();
  const today = todayStr();
  const todayVerses = data[today] || [];
  return {
    todayRecorded: streakStats.todayRecorded,
    versesReadToday: todayVerses.length,
    currentStreak: streakStats.currentStreak,
    longestStreak: streakStats.longestStreak,
    liveStreak: streakStats.liveStreak,
  };
}

/** Remove per-person streak data from this device (called on logout / session expiry). */
export function clearMengajiStreak() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VERSES_READ_KEY);
  } catch {
    /* ignore */
  }
}
